import type { TFunction } from "i18next";
import {
  CircleAlert,
  CircleCheck,
  CircleHelp,
  type LucideIcon,
  TriangleAlert,
} from "lucide-react";
import {
  AgentBusinessUnit,
  AgentEstateGroup,
  AgentGroupDimension,
  AgentIdentityProvider,
  AgentMissionAction,
  AgentMissionConfidenceLevel,
  AgentMissionEvidenceType,
  AgentPlatform,
  AgentPurpose,
  AgentSecurityStatus,
  DiscoveryStageId,
  EnforcementLayer,
  EnforcementReadinessStatus,
  EstateAgentToolClassification,
  EstateAgentToolRisk,
  PolicyCategory,
  PolicyControlSense,
  RecommendationStrength,
} from "@/api/temp/AgentEstateService";
import { type Tone } from "@/components/StatusBadge/StatusBadge";
import { URLs } from "@/configs/menu";
import { mcpServerDetailsPath } from "@/pages/MCPRegistry/utils/mcpServerDetailsPath";

/**
 * Three security states, everywhere. Keyed by uppercased status, per
 * StatusBadge. The label is always rendered as text - colour alone never
 * carries the difference between secure and not secure.
 *
 * These live in the feature's own i18n namespace rather than the shared
 * `statusBadge.*` one: nothing outside the agent estate reports a
 * policy-based access security status.
 *
 * The API enum says SECURE / NOT_SECURE, but what it measures is policy
 * coverage, so the UI names it that (Policy Covered / Policy Gaps / Not Yet
 * Evaluated) and never claims an agent is "secure" in the broader sense.
 * TODO(backend): confirm the enum's semantics and rename it to match if the
 * contract allows; until then the mapping lives only in these labels.
 */
export const AGENT_SECURITY_STATUS_LABELS: Record<string, string> = {
  SECURE: "agentEstate.securityStatus.secure",
  NOT_SECURE: "agentEstate.securityStatus.notSecure",
  NOT_EVALUATED: "agentEstate.securityStatus.notEvaluated",
};

/**
 * What the agent list's `securityStatus` filter can be named. `NEEDS_ATTENTION`
 * is a drill-down grouping no agent carries, so it lives here rather than in
 * `AGENT_SECURITY_STATUS_LABELS`, which badges and group labels read.
 */
export const SECURITY_STATUS_FILTER_LABELS: Record<string, string> = {
  ...AGENT_SECURITY_STATUS_LABELS,
  NEEDS_ATTENTION: "agentEstate.securityStatus.needsAttention",
};

export const AGENT_SECURITY_STATUS_TONES: Record<string, Tone> = {
  SECURE: "success",
  NOT_SECURE: "warning",
  NOT_EVALUATED: "neutral",
};

/** The tooltip each status carries, so the term is never left to interpretation. */
export const AGENT_SECURITY_STATUS_TOOLTIPS: Record<string, string> = {
  SECURE: "agentEstate.securityStatus.secureTooltip",
  NOT_SECURE: "agentEstate.securityStatus.notSecureTooltip",
  NOT_EVALUATED: "agentEstate.securityStatus.notEvaluatedTooltip",
};

/**
 * Intent Policy enforcement, a separate axis from security status: an agent
 * can be Secure while one control behind its policy is still Not mapped.
 */
export const ENFORCEMENT_STATUS_LABELS: Record<string, string> = {
  ENFORCED: "agentEstate.enforcement.status.enforced",
  PARTIALLY_ENFORCED: "agentEstate.enforcement.status.partiallyEnforced",
  NOT_ENFORCED: "agentEstate.enforcement.status.notEnforced",
  NOT_MAPPED: "agentEstate.enforcement.status.notMapped",
  CONFLICT_DETECTED: "agentEstate.enforcement.status.conflictDetected",
  NOT_YET_EFFECTIVE: "agentEstate.enforcement.status.notYetEffective",
};

export const ENFORCEMENT_STATUS_TONES: Record<string, Tone> = {
  ENFORCED: "success",
  PARTIALLY_ENFORCED: "warning",
  NOT_ENFORCED: "neutral",
  NOT_MAPPED: "neutral",
  CONFLICT_DETECTED: "error",
  NOT_YET_EFFECTIVE: "info",
};

export const ENFORCEMENT_STATUS_TOOLTIPS: Record<string, string> = {
  ENFORCED: "agentEstate.enforcement.status.enforcedTooltip",
  PARTIALLY_ENFORCED: "agentEstate.enforcement.status.partiallyEnforcedTooltip",
  NOT_ENFORCED: "agentEstate.enforcement.status.notEnforcedTooltip",
  NOT_MAPPED: "agentEstate.enforcement.status.notMappedTooltip",
  CONFLICT_DETECTED: "agentEstate.enforcement.status.conflictDetectedTooltip",
  NOT_YET_EFFECTIVE: "agentEstate.enforcement.status.notYetEffectiveTooltip",
};

/**
 * TrustLogix enforcement readiness - can TrustLogix enforce policies on this
 * agent's runtime traffic. A third status system, separate from policy
 * coverage (`AGENT_SECURITY_STATUS_*`) and from one Intent Policy's
 * enforcement (`ENFORCEMENT_STATUS_*`); never merged into either.
 *
 * Every state carries an icon and its label, so none relies on colour.
 */
export const ENFORCEMENT_READINESS_LABELS: Record<string, string> = {
  READY: "agentEstate.enforcementReadiness.status.ready",
  SETUP_REQUIRED: "agentEstate.enforcementReadiness.status.setupRequired",
  ATTENTION_REQUIRED:
    "agentEstate.enforcementReadiness.status.attentionRequired",
  UNKNOWN: "agentEstate.enforcementReadiness.status.unknown",
};

export const ENFORCEMENT_READINESS_TONES: Record<string, Tone> = {
  READY: "success",
  SETUP_REQUIRED: "warning",
  ATTENTION_REQUIRED: "error",
  UNKNOWN: "neutral",
};

export const ENFORCEMENT_READINESS_ICONS: Record<string, LucideIcon> = {
  READY: CircleCheck,
  SETUP_REQUIRED: TriangleAlert,
  ATTENTION_REQUIRED: CircleAlert,
  UNKNOWN: CircleHelp,
};

export const ENFORCEMENT_READINESS_TOOLTIPS: Record<string, string> = {
  READY: "agentEstate.enforcementReadiness.tooltip.ready",
  SETUP_REQUIRED: "agentEstate.enforcementReadiness.tooltip.setupRequired",
  ATTENTION_REQUIRED:
    "agentEstate.enforcementReadiness.tooltip.attentionRequired",
  UNKNOWN: "agentEstate.enforcementReadiness.tooltip.unknown",
};

/** How an agent's runtime traffic reaches TrustLogix enforcement. */
export const ENFORCEMENT_METHOD_LABELS: Record<string, string> = {
  MCP_GATEWAY: "agentEstate.enforcementReadiness.method.mcpGateway",
  PLUGIN: "agentEstate.enforcementReadiness.method.plugin",
  OTHER: "agentEstate.enforcementReadiness.method.other",
  NONE: "agentEstate.enforcementReadiness.method.none",
};

/** An enforcement path's connection health. */
export const ENFORCEMENT_HEALTH_LABELS: Record<string, string> = {
  HEALTHY: "agentEstate.enforcementReadiness.health.healthy",
  UNHEALTHY: "agentEstate.enforcementReadiness.health.unhealthy",
  UNKNOWN: "agentEstate.enforcementReadiness.health.unknown",
};

/**
 * The agent-list heading for an `enforcementStatus` filter. `SETUP_GAP` is what
 * every "need enforcement setup" count counts (known-incomplete setup, unknown
 * excluded) and `UNKNOWN` is what every "not verified" count counts, so a card
 * or banner count and the list it opens always agree. `NOT_READY` (everything
 * that is not configured) stays so existing links keep working.
 */
export const ENFORCEMENT_READINESS_FILTER_LABELS: Record<string, string> = {
  READY: ENFORCEMENT_READINESS_LABELS.READY,
  SETUP_GAP: "agentEstate.enforcementReadiness.filter.setupGap",
  UNKNOWN: ENFORCEMENT_READINESS_LABELS.UNKNOWN,
  NOT_READY: "agentEstate.enforcementReadiness.filter.notReady",
  SETUP_REQUIRED: ENFORCEMENT_READINESS_LABELS.SETUP_REQUIRED,
  ATTENTION_REQUIRED: ENFORCEMENT_READINESS_LABELS.ATTENTION_REQUIRED,
};

/** The filter values the agent list offers, in display order. */
export const ENFORCEMENT_FILTER_OPTIONS = [
  "READY",
  "SETUP_GAP",
  "UNKNOWN",
  "NOT_READY",
] as const;

/** A readiness status, or a drill-down grouping of them. */
export type EnforcementStatusFilter =
  | EnforcementReadinessStatus
  | "SETUP_GAP"
  | "NOT_READY";

export const ENFORCEMENT_LAYER_LABELS: Record<EnforcementLayer, string> = {
  AGENT: "agentEstate.enforcement.layer.agent",
  SERVER: "agentEstate.enforcement.layer.server",
  TOOL: "agentEstate.enforcement.layer.tool",
  DATA: "agentEstate.enforcement.layer.data",
};

export const SECURITY_STATUSES: SecurityStatusFilter[] = [
  "SECURE",
  "NOT_SECURE",
  "NOT_EVALUATED",
  "NEEDS_ATTENTION",
];

/**
 * A security status, or the drill-down grouping of the two that call for
 * action: `NEEDS_ATTENTION` is policy gaps plus not-yet-evaluated agents - what
 * a category card's "need additional policy protection" count reports. Mirrors
 * how `NOT_READY` groups enforcement statuses.
 */
export type SecurityStatusFilter = AgentSecurityStatus | "NEEDS_ATTENTION";

/**
 * Whether an agent's status passes a `securityStatus` filter. The one place the
 * `NEEDS_ATTENTION` grouping is defined, so the list and the card's count agree.
 */
export const matchesSecurityStatusFilter = (
  status: AgentSecurityStatus,
  filter: string | undefined,
): boolean => {
  if (!filter) return true;
  return filter === "NEEDS_ATTENTION" ? status !== "SECURE" : status === filter;
};

export const PLATFORM_LABEL_KEYS: Record<AgentPlatform, string> = {
  MICROSOFT_FOUNDRY: "agentEstate.platform.microsoftFoundry",
  MICROSOFT_365: "agentEstate.platform.microsoft365",
  OPENAI: "agentEstate.platform.openAi",
  DATABRICKS: "agentEstate.platform.databricks",
  SNOWFLAKE: "agentEstate.platform.snowflake",
  AWS_AGENTCORE: "agentEstate.platform.awsAgentCore",
  GLEAN: "agentEstate.platform.glean",
};

export const BUSINESS_UNIT_LABEL_KEYS: Record<AgentBusinessUnit, string> = {
  HR: "agentEstate.businessUnit.hr",
  CUSTOMER_SUPPORT: "agentEstate.businessUnit.customerSupport",
  FINANCE: "agentEstate.businessUnit.finance",
  ENGINEERING: "agentEstate.businessUnit.engineering",
  ANALYTICS: "agentEstate.businessUnit.analytics",
};

/** What the agent is intended to do - distinct from the business unit that owns it. */
export const PURPOSE_LABEL_KEYS: Record<AgentPurpose, string> = {
  ENGINEERING: "agentEstate.purpose.engineering",
  ANALYTICS: "agentEstate.purpose.analytics",
  RESEARCH: "agentEstate.purpose.research",
  HR: "agentEstate.purpose.hr",
  FINANCE: "agentEstate.purpose.finance",
  CUSTOMER_SUPPORT: "agentEstate.purpose.customerSupport",
  CONTENT_CREATION: "agentEstate.purpose.contentCreation",
};

export const POLICY_CATEGORY_LABEL_KEYS: Record<PolicyCategory, string> = {
  PRIVACY_PROTECTION: "agentEstate.policyCategory.privacyProtection",
  REGULATORY_COMPLIANCE: "agentEstate.policyCategory.regulatoryCompliance",
  BUSINESS_RULES: "agentEstate.policyCategory.businessRules",
  TOOL_ACTION_CONTROLS: "agentEstate.policyCategory.toolActionControls",
  DATA_ACCESS_CONTROLS: "agentEstate.policyCategory.dataAccessControls",
};

export const POLICY_CATEGORIES: PolicyCategory[] = [
  "PRIVACY_PROTECTION",
  "REGULATORY_COMPLIANCE",
  "BUSINESS_RULES",
  "TOOL_ACTION_CONTROLS",
  "DATA_ACCESS_CONTROLS",
];

export const RECOMMENDATION_STRENGTH_LABEL_KEYS: Record<
  RecommendationStrength,
  string
> = {
  STRONGLY_RECOMMENDED: "agentEstate.recommendation.stronglyRecommended",
  RECOMMENDED: "agentEstate.recommendation.recommended",
};

export const POLICY_CONTROL_SENSE_LABEL_KEYS: Record<
  PolicyControlSense,
  string
> = {
  ALLOW: "agentEstate.recommendation.allow",
  BLOCK: "agentEstate.recommendation.block",
};

export const GROUP_DIMENSION_LABEL_KEYS: Record<AgentGroupDimension, string> = {
  NONE: "agentEstate.categories.dimension.none",
  BUSINESS_UNIT: "agentEstate.categories.dimension.businessUnit",
  PROJECT: "agentEstate.categories.dimension.project",
  ENVIRONMENT: "agentEstate.categories.dimension.environment",
  PLATFORM: "agentEstate.categories.dimension.platform",
  CONNECTED_RESOURCE: "agentEstate.categories.dimension.connectedResource",
  PURPOSE: "agentEstate.categories.dimension.purpose",
  SECURITY_STATUS: "agentEstate.categories.dimension.securityStatus",
};

/**
 * Connected resources the estate can be filtered by. Mirrors the mock's
 * `CONNECTED_RESOURCES`; a page must not import from `api/mock-data`, and the
 * real API will return these as filter metadata.
 */
export const CONNECTED_RESOURCE_OPTIONS = [
  "SharePoint",
  "Snowflake",
  "Databricks",
  "Internal Knowledge Base",
  "GitHub",
  "Microsoft 365",
];

/**
 * Delivery initiatives the estate can be filtered by. Mirrors the mock's
 * `PROJECT_OPTIONS` for the same reason as the list above: a page must not
 * import from `api/mock-data`, and the real API will return these as filter
 * metadata.
 */
export const PROJECT_OPTIONS = [
  "Client Onboarding (KYC)",
  "Wealth Advisory Portal",
  "Trade Surveillance",
  "Trading Platform Modernization",
  "Research Publishing",
];

/** Deployment tiers the estate can be filtered by. */
export const ENVIRONMENT_TYPE_OPTIONS = ["PRODUCTION", "TEST", "DEVELOPMENT"];

/** Ordered as the Group by dropdown lists them; the first is the default. */
export const GROUP_DIMENSIONS: AgentGroupDimension[] = [
  "BUSINESS_UNIT",
  "PROJECT",
  "ENVIRONMENT",
  "PLATFORM",
  "PURPOSE",
  "SECURITY_STATUS",
  "CONNECTED_RESOURCE",
  "NONE",
];

/**
 * The dimensions the category cards can group by. `NONE` and `SECURITY_STATUS`
 * are list-only: one card covering everything is not a category view, and
 * grouping cards by the very status they report is circular.
 */
export const CATEGORY_DIMENSIONS: AgentGroupDimension[] = [
  "BUSINESS_UNIT",
  "PROJECT",
  "ENVIRONMENT",
  "PLATFORM",
  "PURPOSE",
  "CONNECTED_RESOURCE",
];

/** The list filter param each group dimension narrows the agent list by. */
export const DIMENSION_FILTER_KEYS: Record<AgentGroupDimension, string> = {
  NONE: "",
  PLATFORM: "platform",
  BUSINESS_UNIT: "businessUnit",
  PROJECT: "project",
  // `environmentType`, not `environment`: `environmentId` is already a
  // first-class param on every estate call, and `?environment=` beside it
  // would read as the connection rather than the deployment tier.
  ENVIRONMENT: "environmentType",
  CONNECTED_RESOURCE: "connectedResource",
  PURPOSE: "purpose",
  SECURITY_STATUS: "securityStatus",
};

export const DISCOVERY_STAGE_LABEL_KEYS: Record<DiscoveryStageId, string> = {
  IMPORTING: "agentEstate.progress.stage.importing",
  LINKING_MCP: "agentEstate.progress.stage.linkingMcp",
  RESOLVING_IDENTITY: "agentEstate.progress.stage.resolvingIdentity",
  METADATA: "agentEstate.progress.stage.metadata",
  ANALYZING: "agentEstate.progress.stage.analyzing",
  CLASSIFYING: "agentEstate.progress.stage.classifying",
  POLICY_GAPS: "agentEstate.progress.stage.policyGaps",
  RECOMMENDATIONS: "agentEstate.progress.stage.recommendations",
};

export const IDENTITY_PROVIDER_LABEL_KEYS: Record<string, string> = {
  MICROSOFT_ENTRA_ID: "agentEstate.identityProvider.microsoftEntraId",
  OKTA: "agentEstate.identityProvider.okta",
  PING_IDENTITY: "agentEstate.identityProvider.pingIdentity",
  SAVIYNT: "agentEstate.identityProvider.saviynt",
  SAILPOINT: "agentEstate.identityProvider.sailpoint",
  AWS_IAM: "investmentBankingDemo.awsIam",
  OTHER: "agentEstate.identityProvider.other",
};

/**
 * The identity providers offered as tiles on the wizard's identity step.
 *
 * Deliberately not derived from `IDENTITY_PROVIDER_LABEL_KEYS`: that map also
 * carries the deferral option, and a "Configure later" tile alongside Entra and
 * Okta would read as a kind of provider rather than as a decision to skip.
 * Deferring stays a secondary action beside Continue.
 */
export interface IdentityProviderCatalogEntry {
  id: AgentIdentityProvider;
  labelKey: string;
  descriptionKey: string;
  availability: "AVAILABLE" | "COMING_SOON";
  /**
   * Offered as Beta in the investment-banking demo (`isInvestmentBankingDemo`:
   * agent demo mode with the onboarding flag), through the simulated
   * format-only credential check (`validateDemoIdentity`). Everywhere else it
   * stays Coming soon: there is no connector behind it.
   */
  demoFormatCheck?: boolean;
}

/** Providers whose demo check validates credential format, not authentication. */
export const isFormatCheckedIdentityProvider = (id?: unknown): boolean =>
  IDENTITY_PROVIDER_CATALOG.some(
    entry => entry.id === id && entry.demoFormatCheck,
  );

export const IDENTITY_PROVIDER_CATALOG: IdentityProviderCatalogEntry[] = [
  {
    id: "MICROSOFT_ENTRA_ID",
    labelKey: "agentEstate.identityProvider.microsoftEntraId",
    descriptionKey: "agentEstate.wizard.identity.entraDescription",
    availability: "AVAILABLE",
  },
  {
    id: "OKTA",
    labelKey: "agentEstate.identityProvider.okta",
    descriptionKey: "agentEstate.wizard.identity.oktaDescription",
    availability: "COMING_SOON",
    demoFormatCheck: true,
  },
  {
    id: "PING_IDENTITY",
    labelKey: "agentEstate.identityProvider.pingIdentity",
    descriptionKey: "agentEstate.wizard.identity.pingIdentityDescription",
    availability: "COMING_SOON",
    demoFormatCheck: true,
  },
  {
    id: "SAVIYNT",
    labelKey: "agentEstate.identityProvider.saviynt",
    descriptionKey: "agentEstate.wizard.identity.saviyntDescription",
    availability: "COMING_SOON",
    demoFormatCheck: true,
  },
  {
    id: "SAILPOINT",
    labelKey: "agentEstate.identityProvider.sailpoint",
    descriptionKey: "agentEstate.wizard.identity.sailpointDescription",
    availability: "COMING_SOON",
    demoFormatCheck: true,
  },
  {
    id: "AWS_IAM",
    labelKey: "investmentBankingDemo.awsIam",
    descriptionKey: "investmentBankingDemo.awsIamDescription",
    availability: "COMING_SOON",
  },
];

export const ENVIRONMENT_TYPE_LABEL_KEYS: Record<string, string> = {
  PRODUCTION: "agentEstate.environmentType.production",
  TEST: "agentEstate.environmentType.test",
  DEVELOPMENT: "agentEstate.environmentType.development",
};

/**
 * How often the progress panel re-reads the run - the UI's sampling rate, NOT
 * how long discovery takes. The run length is DISCOVERY_DURATION_MS in
 * `api/mock-data/agent-estate/phases.ts`; change that to make onboarding
 * slower or faster.
 *
 * This has to stay well under the duration, or the panel holds a stale
 * snapshot and then jumps straight to complete.
 */
export const DISCOVERY_POLL_MS = 1000 * 2; // 2 seconds
/**
 * A policy deployment moves through its steps in about a second each, so it
 * is polled faster than discovery - and only while a run is in flight.
 */
export const IMPLEMENTATION_POLL_MS = 400;
/** Against the real backend, a deployment in flight is polled every 2 seconds. */
export const LIVE_IMPLEMENTATION_POLL_MS = 1000 * 2;

export const AGENT_LIST_PAGE_SIZE = 25;

/**
 * List filters live mode neither offers nor sends: business unit has no
 * GET /api/v2/agents param (backend will not add one), Project / Connected Resource offer only
 * demo seed options, and recommendations are empty in live this sprint.
 */
export const DEMO_ONLY_AGENT_LIST_FILTERS = [
  "businessUnit",
  "connectedResource",
  "project",
  "hasRecommendations",
] as const;

/* --------------------------------------------------------- agent mission */

/**
 * The mission's governance state. Orthogonal to the security status above: a
 * CONFIRMED mission says an administrator vouches for what the agent is *for*,
 * not that the controls around it are in place.
 */
export const MISSION_STATUS_LABELS: Record<string, string> = {
  ADMINISTRATOR_DEFINED:
    "agentEstate.details.mission.status.administratorDefined",
  INFERRED_NEEDS_REVIEW:
    "agentEstate.details.mission.status.inferredNeedsReview",
  CONFIRMED: "agentEstate.details.mission.status.confirmed",
  OVERRIDDEN: "agentEstate.details.mission.status.overridden",
  NEEDS_MORE_CONTEXT: "agentEstate.details.mission.status.needsMoreContext",
  REVIEW_RECOMMENDED: "agentEstate.details.mission.status.reviewRecommended",
};

export const MISSION_STATUS_TONES: Record<string, Tone> = {
  ADMINISTRATOR_DEFINED: "success",
  CONFIRMED: "success",
  OVERRIDDEN: "success",
  INFERRED_NEEDS_REVIEW: "info",
  NEEDS_MORE_CONTEXT: "warning",
  REVIEW_RECOMMENDED: "warning",
};

export const MISSION_STATUS_TOOLTIPS: Record<string, string> = {
  ADMINISTRATOR_DEFINED:
    "agentEstate.details.mission.statusTooltip.administratorDefined",
  INFERRED_NEEDS_REVIEW:
    "agentEstate.details.mission.statusTooltip.inferredNeedsReview",
  CONFIRMED: "agentEstate.details.mission.statusTooltip.confirmed",
  OVERRIDDEN: "agentEstate.details.mission.statusTooltip.overridden",
  NEEDS_MORE_CONTEXT:
    "agentEstate.details.mission.statusTooltip.needsMoreContext",
  REVIEW_RECOMMENDED:
    "agentEstate.details.mission.statusTooltip.reviewRecommended",
};

/** One snackbar per action, picked from the action the store echoes back. */
export const MISSION_ACTION_SUCCESS_KEYS: Record<AgentMissionAction, string> = {
  CONFIRM: "agentEstate.details.mission.toast.confirmed",
  OVERRIDE: "agentEstate.details.mission.toast.overridden",
  NEEDS_MORE_CONTEXT: "agentEstate.details.mission.toast.deferred",
  KEEP_CURRENT: "agentEstate.details.mission.toast.kept",
  ACCEPT_SUGGESTED: "agentEstate.details.mission.toast.updated",
};

export const MISSION_EVIDENCE_TYPE_LABEL_KEYS: Record<
  AgentMissionEvidenceType,
  string
> = {
  IDENTITY: "agentEstate.details.mission.evidenceType.identity",
  RUNTIME: "agentEstate.details.mission.evidenceType.runtime",
  MCP: "agentEstate.details.mission.evidenceType.mcp",
  TOOL: "agentEstate.details.mission.evidenceType.tool",
  DATA: "agentEstate.details.mission.evidenceType.data",
  SEMANTIC: "agentEstate.details.mission.evidenceType.semantic",
};

export const MISSION_CONFIDENCE_LABEL_KEYS: Record<
  AgentMissionConfidenceLevel,
  string
> = {
  HIGH: "agentEstate.details.mission.confidence.high",
  MEDIUM: "agentEstate.details.mission.confidence.medium",
  LOW: "agentEstate.details.mission.confidence.low",
};

/**
 * The Edit Mission drawer's purpose options. The same closed vocabulary the
 * category cards and the recommendation selectors use, so an override
 * re-targets the agent rather than inventing a category nothing can group by.
 */
// Derived from the label map rather than re-listed, so a new purpose cannot
// reach the estate without also being offerable in the Edit drawer.
export const MISSION_PURPOSE_OPTIONS = Object.keys(
  PURPOSE_LABEL_KEYS,
) as AgentPurpose[];

/**
 * The label map each group dimension reads values through, so a dimension is
 * named in one place rather than in an if-chain per call site. Dimensions with
 * no fixed vocabulary (CONNECTED_RESOURCE is free text, NONE has no value) are
 * absent, and callers fall back to the raw value.
 */
export const DIMENSION_LABEL_MAPS: Partial<
  Record<AgentGroupDimension, Record<string, string>>
> = {
  PLATFORM: PLATFORM_LABEL_KEYS,
  BUSINESS_UNIT: BUSINESS_UNIT_LABEL_KEYS,
  PURPOSE: PURPOSE_LABEL_KEYS,
  SECURITY_STATUS: AGENT_SECURITY_STATUS_LABELS,
  ENVIRONMENT: ENVIRONMENT_TYPE_LABEL_KEYS,
  // PROJECT is absent on purpose: project names are tenant-authored free text,
  // so callers fall back to the raw value rather than rendering a missing key.
};

/**
 * Every dimension filter, cleared. Spread before setting one, so switching
 * category never leaves the previous dimension's filter silently applied.
 */
export const CLEARED_DIMENSION_FILTERS: Record<string, string | null> = {
  platform: null,
  businessUnit: null,
  project: null,
  environmentType: null,
  connectedResource: null,
  purpose: null,
  securityStatus: null,
  enforcementStatus: null,
};

/* ------------------------------------------------------------------ routes */

/** The Agent Details tab hidden in live mode this sprint. */
export const ACCESS_FLOW_TAB = "access-flow";

/**
 * The agent-details route, in one place. Defaulted to the Overview tab, which
 * is where every link that does not name a tab is meant to land.
 */
export const agentDetailsPath = (
  id: string,
  type = "overview",
  returnTo?: string,
): string => {
  const path = URLs.agent_estate_details_type
    .replace(":id", id)
    .replace(":type", type);
  if (!returnTo) return path;
  const url = new URL(path, "http://localhost");
  url.searchParams.set("returnTo", returnTo);
  return `${url.pathname}${url.search}${url.hash}`;
};

/** Only a list route can be restored, never an arbitrary external URL. */
export const agentListReturnPath = (search: string): string => {
  const candidate = new URLSearchParams(search).get("returnTo") ?? "";
  return candidate === URLs.agent_estate ||
    candidate.startsWith(`${URLs.agent_estate}?`)
    ? candidate
    : URLs.agent_estate;
};

/**
 * The real Data Sources list, optionally scoped to one data-source type and
 * one risk category. The board's data-platform cards and estate-inventory rows
 * are the callers: they hand over a lowercase `AccountTypes` key, which the
 * list page reads back as a `dataSourceType` QueryBar filter, and the API's
 * category key as its `dataRiskCategory` filter.
 */
export const dataSourcesPath = (
  dataSourceType?: string,
  dataRiskCategory?: string,
): string => {
  const params = new URLSearchParams();
  if (dataSourceType) params.set("dataSourceType", dataSourceType);
  if (dataRiskCategory) params.set("dataRiskCategory", dataRiskCategory);
  const query = params.toString();
  return query ? `${URLs.accounts}?${query}` : URLs.accounts;
};

/** A data source's access policies - where its controls are authored. */
export const dataSourcePoliciesPath = (sourceId: string): string =>
  URLs.account_details_type
    .replace(":id", sourceId)
    .replace(":type", "access_policies");

/**
 * One MCP server's policies, on its details page, when the registry knows the
 * server; the MCP Registry otherwise. MCP policy authoring stays there; this
 * only points at it.
 */
export const mcpServerPolicyPath = (registryServerId?: string): string =>
  registryServerId
    ? mcpServerDetailsPath(registryServerId, "policies")
    : URLs.mcp_type.replace(":type", "registry");

export const dashboardCategoryPath = (dimension = "BUSINESS_UNIT"): string => {
  const params = new URLSearchParams({ estate: "ai", groupBy: dimension });
  return `${URLs.dashboard}?${params}`;
};

/** The agent list scoped to the agents imported through one registry. */
export const agentsInRegistryPath = (registryId: string): string =>
  `${URLs.agent_estate}?${new URLSearchParams({ agentRegistryId: registryId })}`;

/** The estate list filtered to agents carrying recommendations — the app's
 * "everything that needs governance attention" view. Lives here with the
 * params it encodes, so a rename of `view`/`hasRecommendations` can't leave
 * an outside caller silently linking to an unfiltered estate. */
export const agentsNeedingAttentionPath = (): string =>
  `${URLs.agent_estate}?view=all&hasRecommendations=true`;

/**
 * The estate list, scoped to one category. The Security Overview board's
 * Explore cards are the only external caller; the estate page itself patches
 * its own params in place (`showGroupAgents`) rather than navigating, so the
 * two have to agree on the param names — which is why this lives beside
 * `DIMENSION_FILTER_KEYS` rather than at the call site.
 *
 * `URLSearchParams` is not optional here: CONNECTED_RESOURCE values are free
 * text and contain spaces ("Internal Knowledge Base", "Microsoft 365"), which
 * template-literal interpolation would emit raw into the query string.
 */
export const estateGroupPath = (
  dimension: AgentGroupDimension,
  value: string,
  options: {
    hasRecommendations?: boolean;
    securityStatus?: SecurityStatusFilter;
    /** A readiness status, `SETUP_GAP`, or `NOT_READY` (everything but READY). */
    enforcementStatus?: EnforcementStatusFilter;
  } = {},
): string => {
  const params = new URLSearchParams({ view: "all" });
  const key = DIMENSION_FILTER_KEYS[dimension];
  // NONE maps to "" — there is no filter param for "not grouped", so the link
  // degrades to the unfiltered list rather than emitting `?=HR`.
  if (key && value) params.set(key, value);
  if (options.securityStatus)
    params.set("securityStatus", options.securityStatus);
  if (options.hasRecommendations) params.set("hasRecommendations", "true");
  if (options.enforcementStatus)
    params.set("enforcementStatus", options.enforcementStatus);
  return `${URLs.agent_estate}?${params.toString()}`;
};

/**
 * The estate list narrowed to agents whose enforcement setup is known to be
 * incomplete - exactly the population every "need enforcement setup" count
 * reports. Agents whose setup could not be read are not in it; they have their
 * own drill-down (`agentsWithUnverifiedEnforcementPath`).
 */
export const agentsNeedingEnforcementPath = (): string =>
  estateGroupPath("NONE", "", { enforcementStatus: "SETUP_GAP" });

/** The estate list narrowed to agents whose enforcement setup is not verified. */
export const agentsWithUnverifiedEnforcementPath = (): string =>
  estateGroupPath("NONE", "", { enforcementStatus: "UNKNOWN" });

/**
 * The i18n key naming one group value, or `undefined` when the dimension has
 * no fixed vocabulary (CONNECTED_RESOURCE is free text) — callers render the
 * raw value in that case. Shared so the estate page and the board resolve
 * category names through one lookup instead of two copies of it.
 */
export const dimensionValueLabelKey = (
  dimension: AgentGroupDimension,
  value: string,
): string | undefined => DIMENSION_LABEL_MAPS[dimension]?.[value];

/** A category value's display name; the raw value when it has no label. */
export const groupValueLabel = (
  t: TFunction,
  dimension: AgentGroupDimension,
  value: string,
): string => {
  const key = dimensionValueLabelKey(dimension, value);
  return key ? t(key) : value;
};

/** Splits a group key (`BUSINESS_UNIT:CUSTOMER_SUPPORT`) into its parts. */
export const parseGroupKey = (
  key: string,
): { dimension: AgentGroupDimension; value: string } => {
  const separator = key.indexOf(":");
  return {
    dimension: key.slice(0, separator) as AgentGroupDimension,
    value: key.slice(separator + 1),
  };
};

/** The test-id slug for a category value, shared by every card surface. */
export const categorySlug = (value: string): string =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, "-");

/** The filtered list has one label in its heading and in detail breadcrumbs. */
export const agentListScopeLabel = (
  params: URLSearchParams,
  t: TFunction,
): string => {
  const dimension = CATEGORY_DIMENSIONS.find(item =>
    params.get(DIMENSION_FILTER_KEYS[item]),
  );
  const value = dimension ? params.get(DIMENSION_FILTER_KEYS[dimension])! : "";
  const labelKey = dimension
    ? dimensionValueLabelKey(dimension, value)
    : undefined;
  const status =
    SECURITY_STATUS_FILTER_LABELS[params.get("securityStatus") ?? ""];
  const enforcement =
    ENFORCEMENT_READINESS_FILTER_LABELS[params.get("enforcementStatus") ?? ""];
  return (
    [
      labelKey ? t(labelKey) : value,
      status ? t(status) : "",
      enforcement ? t(enforcement) : "",
    ]
      .filter(Boolean)
      .join(" · ") || t("agentEstate.list.allAgents")
  );
};

/**
 * The platform a category card speaks for.
 *
 * Grouped by Technology / Platform the card *is* a platform, so it names its
 * own. Under every other lens it borrows the connected environment's: an HR
 * business unit spans no single platform, but every agent counted on that card
 * still runs on the one environment the estate is reading from.
 *
 * Shared so the board and the estate page resolve it identically - the card is
 * rendered by both, and a mark that differed between them would be describing
 * the same agents two ways.
 */
export const groupPlatform = (
  group: AgentEstateGroup,
  environmentPlatform: AgentPlatform,
): AgentPlatform =>
  group.dimension === "PLATFORM" && group.value in PLATFORM_LABEL_KEYS
    ? (group.value as AgentPlatform)
    : environmentPlatform;

/* --------------------------------------------------- connected resources */

/** Per-capability policy state used by the Resources tree and tool drawer. */
export const AGENT_RESOURCE_STATUS_LABELS: Record<string, string> = {
  PROTECTED: "agentEstate.details.resourcesTab.status.protected",
  REVIEW: "agentEstate.details.resourcesTab.status.review",
  NEEDS_POLICY: "agentEstate.details.resourcesTab.status.needsPolicy",
  RESTRICTED: "agentEstate.details.resourcesTab.status.restricted",
  DENIED: "agentEstate.details.resourcesTab.status.denied",
};

export const AGENT_RESOURCE_STATUS_TONES: Record<string, Tone> = {
  PROTECTED: "success",
  REVIEW: "warning",
  NEEDS_POLICY: "warning",
  RESTRICTED: "info",
  DENIED: "error",
};

/**
 * Tool risk and capability arrive from the service as display-cased literals
 * ("High", "Destructive"). They still have to go through i18n - the filter
 * dropdowns above the tree translate the same vocabulary, so rendering the raw
 * value would put translated and untranslated copies of one word side by side.
 */
export const AGENT_TOOL_RISK_LABEL_KEYS: Record<EstateAgentToolRisk, string> = {
  Low: "agentEstate.details.resourcesTab.risk.low",
  Medium: "agentEstate.details.resourcesTab.risk.medium",
  High: "agentEstate.details.resourcesTab.risk.high",
};

export const AGENT_TOOL_CLASSIFICATION_LABEL_KEYS: Record<
  EstateAgentToolClassification,
  string
> = {
  Read: "agentEstate.details.resourcesTab.classification.read",
  Write: "agentEstate.details.resourcesTab.classification.write",
  Destructive: "agentEstate.details.resourcesTab.classification.destructive",
  Privileged: "agentEstate.details.resourcesTab.classification.privileged",
};

/** Display order for capability / risk filters, from the label maps above. */
export const AGENT_TOOL_CLASSIFICATIONS = Object.keys(
  AGENT_TOOL_CLASSIFICATION_LABEL_KEYS,
) as EstateAgentToolClassification[];
export const AGENT_TOOL_RISKS = Object.keys(
  AGENT_TOOL_RISK_LABEL_KEYS,
) as EstateAgentToolRisk[];

/** Pill tones for the Resources tool table, keyed like the label maps above. */
export const AGENT_TOOL_RISK_TONES: Record<EstateAgentToolRisk, Tone> = {
  Low: "success",
  Medium: "warning",
  High: "error",
};

export const AGENT_TOOL_CLASSIFICATION_TONES: Record<
  EstateAgentToolClassification,
  Tone
> = {
  Read: "info",
  Write: "cyan",
  Destructive: "error",
  Privileged: "warning",
};

/** Draft vs applied, for authored agent policies. */
export const AUTHORED_POLICY_STATUS_LABEL_KEYS = {
  DRAFT: "agentEstate.details.policyCoverage.authored.draft",
  APPLIED: "agentEstate.details.policyCoverage.authored.applied",
} as const;

/* -------------------------------------------------------- activity decision */

/**
 * Decision outcomes carry their own tones - allow/deny is not a lifecycle.
 * Shared by the Activity tab's grid and the Overview's activity preview, so one
 * decision never reads two ways on the same page.
 */
export const AGENT_DECISION_TONES: Record<string, Tone> = {
  RECORDED: "info",
  ALLOWED: "success",
  DENIED: "error",
  WARNING: "warning",
};

/* Past tense: this is a log of decisions already made, not a rule. */
export const AGENT_DECISION_LABEL_KEYS: Record<string, string> = {
  RECORDED: "agentEstate.cleanup.recorded",
  ALLOWED: "statusBadge.allowed",
  DENIED: "statusBadge.denied",
};

/** Editing a service's connection profile needs the right to change MCP Services. */
export const MCP_AUTH_EDIT_AUTHORITY = "APPLICATION:CREATE" as const;
